import React, { useMemo, useState, useEffect } from 'react';
import AttemptCard from './AttemptCard';
import { fetchSessionOptions, fetchVehicleAssessmentData, fetchViolationDetails } from '../services/api';
import violationDescriptions from '../utils/violationDescriptions';
import Modal from './Modal';

function MainContent({ selectedVehicle, userPK }) {
  const [selectedSession, setSelectedSession] = useState('Session 1');
  const [sessionOptions, setSessionOptions] = useState([
    { value: 1, label: 'Session 1' }
  ]);
  const [attempts, setAttempts] = useState([]);
  const [, setIsLoading] = useState(false);
  const [, setMtvData] = useState([]); // Add state for MTV data
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [violationDetails, setViolationDetails] = useState([]);
  const [violationLoading, setViolationLoading] = useState(false);
  const [, setTestTakenDate] = useState(null);
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  // Fetch session options when vehicle changes
  useEffect(() => {
    if (selectedVehicle && userPK) {
      setIsLoading(true);
      
      // Default to Session 1 when vehicle changes (will be overridden if no sessions)
      setSelectedSession('Session 1');
      
      // Reset attempts and other state to ensure a clean slate when vehicle changes
      setAttempts([]);
      setMtvData([]);
      setSelectedAttempt(null);
      setViolationDetails([]);
      setTestTakenDate(null);
      
      // Use the exact ScreenName as vehicleName
      const vehicleName = selectedVehicle;
      
      // Fetch session options from the backend
      fetchSessionOptions(userPK, vehicleName)
        .then(data => {
          if (data.sessionOptions && data.sessionOptions.length > 0) {
            setSessionOptions(data.sessionOptions);
            
            // If we have a "No session available" option, select it
            if (data.sessionOptions[0].label === 'No session available') {
              setSelectedSession('No session available');
            }
          } else {
            // If no sessions found, default to no session available
            const noSessionOption = [{ value: 0, label: 'No session available' }];
            setSessionOptions(noSessionOption);
            setSelectedSession('No session available');
          }
          setIsLoading(false);
        })
        .catch(error => {
          console.error('Error fetching session options:', error);
          // On error, fallback to no session available
          const noSessionOption = [{ value: 0, label: 'No session available' }];
          setSessionOptions(noSessionOption);
          setSelectedSession('No session available');
          setIsLoading(false);
        });
    } else {
      setAttempts([]);
      setSelectedAttempt(null);
      setViolationDetails([]);
    }
  }, [selectedVehicle, userPK]); // Add userPK to dependency array to refetch when user changes

  // Fetch assessment data when vehicle or session changes
  useEffect(() => {
    if (selectedVehicle && userPK) {
      setIsLoading(true);
      
      // Use the exact ScreenName as vehicleName
      const vehicleName = selectedVehicle;
      
      // Extract session number from the selected session label
      let sessionNumber = null;
      if (selectedSession !== 'No session available') {
        // Parse the session number from the string (e.g., "Session 2" -> 2)
        const match = selectedSession.match(/\d+/);
        if (match) {
          sessionNumber = match[0];
          console.log(`Fetching data for session number: ${sessionNumber}`);
        }
      }
      
      // Debug log for assessment API call
      console.log('ASSESSMENT API CALL:', { 
        userPK, 
        vehicleName,
        sessionNumber,
        selectedVehicle,
        selectedSession
      });
      
      // Fetch vehicle assessment data based on userPK, selectedVehicle, and sessionNumber
      fetchVehicleAssessmentData(userPK, vehicleName, sessionNumber)
        .then(data => {
          // Debug log response
          console.log('ASSESSMENT API RESPONSE:', data);
          
          if (data.attempts && data.attempts.length > 0) {
            setAttempts(data.attempts);
            // Select the first attempt by default
            setSelectedAttempt(data.attempts[0]);
          } else {
            console.log('No attempts found in response data');
            setAttempts([]);
            setSelectedAttempt(null);
            setViolationDetails([]);
            setTestTakenDate(null);
          }
          
          // Store MTV data if available
          if (data.violationData && data.violationData.mtvData) {
            setMtvData(data.violationData.mtvData);
          } else {
            setMtvData([]);
          }
          
          setIsLoading(false);
        })
        .catch(error => {
          console.error('Error fetching vehicle assessment data:', error);
          setAttempts([]);
          setMtvData([]);
          setIsLoading(false);
          setSelectedAttempt(null);
          setViolationDetails([]);
          setTestTakenDate(null);
        });
    }
  }, [selectedVehicle, userPK, selectedSession]); // Add selectedSession to dependency array

  // Fetch violation details when an attempt is selected
  useEffect(() => {
    if (selectedAttempt && selectedVehicle && userPK) {
      setViolationLoading(true);
      
      console.log('Selected attempt data:', selectedAttempt);
      
      // Use the vehicleID directly from the attempt object if available
      const vehicleID = selectedAttempt.vehicleID || '1'; // Default to 1 (typically forklift) if not available
      const assessmentNumber = selectedAttempt.assessmentNumber;
      const attemptNumber = selectedAttempt.attemptNumber;
      
      console.log('Fetching violation details with params:', { 
        userPK, 
        assessmentNumber, 
        attemptNumber, 
        vehicleID 
      });
      
      fetchViolationDetails(userPK, assessmentNumber, attemptNumber, vehicleID)
        .then(data => {
          console.log('Violation details API response:', data);
          
          // Check if the response is valid
          if (!data) {
            console.error('Received null or undefined API response');
            setViolationDetails([]);
            setViolationLoading(false);
            setTestTakenDate(null);
            return;
          }
          
          // Check for violations array in the response
          if (data.violations) {
            console.log('Setting violation details:', data.violations);
            
            // Make sure it's an array
            if (Array.isArray(data.violations)) {
              setViolationDetails(data.violations);
              
              // Set the test taken date if available
              if (data.testTakenDate) {
                setTestTakenDate(data.testTakenDate);
              } else {
                setTestTakenDate(null);
              }
            } else {
              console.error('Violations data is not an array:', data.violations);
              setViolationDetails([]);
              setTestTakenDate(null);
            }
          } else {
            console.log('No violations found in API response');
            setViolationDetails([]);
            setTestTakenDate(null);
          }
          setViolationLoading(false);
        })
        .catch(error => {
          console.error('Error fetching violation details:', error);
          setViolationDetails([]);
          setViolationLoading(false);
          setTestTakenDate(null);
        });
    } else {
      console.log('Not fetching violation details - missing required data:', { 
        hasSelectedAttempt: !!selectedAttempt, 
        hasSelectedVehicle: !!selectedVehicle, 
        hasUserPK: !!userPK 
      });
      setViolationDetails([]);
      setTestTakenDate(null);
    }
  }, [selectedAttempt, selectedVehicle, userPK]);

  // Calculate average score when attempts change, and ensure it resets when vehicle or session changes
  const averageScore = useMemo(() => {
    if (attempts.length === 0) return 0;
    
    // Filter out "About to play" entries which have non-numeric scores
    const numericScores = attempts.filter(attempt => typeof attempt.score === 'number');
    
    if (numericScores.length === 0) return 0;
    
    const newAvg = Math.round(numericScores.reduce((acc, curr) => acc + curr.score, 0) / numericScores.length);
    console.log(`Calculated average score for ${selectedVehicle}, ${selectedSession}: ${newAvg}`);
    return newAvg;
  }, [attempts, selectedVehicle, selectedSession]); // Add selectedSession to dependency array

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Average';
    return 'Need Improvement';
  };

  const handleAttemptSelect = (attempt) => {
    // Check if the attempt is in "play" mode (not yet completed)
    const isPlayMode = typeof attempt.score !== 'number' || isNaN(attempt.score);
    
    if (isPlayMode) {
      // Show warning modal
      setModalMessage("You need to complete the assessment first. Go to the VR Module and complete it.");
      setModalOpen(true);
      return; // Don't select the attempt
    }
    
    // Only set the selected attempt if it's not in play mode
    setSelectedAttempt(attempt);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden h-screen pr-0">
      {/* Main Content Area - Using scrollbar-hide to remove scrollbars */}
      <div className="flex-1 flex flex-col lg:flex-row gap-4 overflow-y-auto overflow-x-hidden scrollbar-hide">
        {/* Left Side - Vehicle Selection and Game Area */}
        <div className="flex-1 flex flex-col gap-4">
          {/* Vehicle Selection section removed */}

          <div className="mb-0 sm:mb-2">
            <div className="bg-gradient-to-r from-blue-950 to-red-700 px-4 py-1 rounded-2xl flex flex-col glossy-light shadow-lg">
              <div className="flex items-center justify-between">
                <select
                  value={selectedSession}
                  onChange={(e) => setSelectedSession(e.target.value)}
                  className="bg-transparent text-white text-sm sm:text-xl font-semibold focus:outline-none cursor-pointer"
                >
                  {sessionOptions.map(option => (
                    <option key={option.value} value={option.label} className="text-black bg-gradient-to-r from-blue-950 to-red-700">
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex flex-col lg:flex-row gap-1 sm:gap-2 lg:gap-4 mt-0 sm:mt-2 lg:mt-4">
              <div
                className="flex-shrink-0 w-[150px] sm:w-[180px] md:w-[200px] bg-[#ffffff] rounded-2xl p-6 shadow-[0_10px_15px_-3px_rgba(0,0,0,0.1),0_4px_6px_-2px_rgba(0,0,0,0.05)] text-center mx-auto flex flex-col items-center justify-center"
              >
                <div className="flex flex-col items-center justify-center w-full">
                  <div className="text-lg font-bold text-[#22314f] mb-4">Driving Score</div>
                  <div className="text-5xl font-extrabold text-[#22314f] mb-2">{averageScore}</div>
                  <div className="flex items-center justify-center text-[#22314f] font-semibold mb-4">
                    <span className="w-2 h-2 rounded-full bg-[#22314f] inline-block mr-2"></span>
                    <span>Average Score</span>
                  </div>
                  <div className="text-lg font-bold text-[#22314f] mt-2">{getScoreLabel(averageScore)}</div>
                </div>
              </div>

              {/* Attempt Cards Container */}
              <div className="w-full flex flex-col mt-0 sm:mt-2 lg:mt-4 lg:ml-6">
                <h3 className="text-md sm:text-lg font-semibold mb-3 text-secondary">Session Assessments</h3>
                
                {/* Container with scrollbar outside */}
                <div className="w-full max-w-[600px] overflow-x-auto custom-scrollbar-horizontal pb-2">
                  {/* Content container */}
                  <div className="bg-slate-50 rounded-xl p-3 shadow-[0_10px_15px_-3px_rgba(0,0,0,0.1),0_4px_6px_-2px_rgba(0,0,0,0.05)] min-w-full" style={{ minWidth: 'max-content', paddingRight: '4rem' }}>
                    <div className="flex gap-0 flex-nowrap">
                      {attempts.map((attempt, index) => (
                        <div 
                          key={`${selectedVehicle}-${attempt.id}-${index}`} 
                          className="flex-shrink-0 w-[200px] sm:w-[220px] md:w-[240px] cursor-pointer"
                          onClick={() => handleAttemptSelect(attempt)}
                        >
                          <AttemptCard 
                            attempt={attempt} 
                            index={index} 
                            attemptNumber={index + 1}
                            isSelected={selectedAttempt && selectedAttempt.id === attempt.id}
                          />
                        </div>
                      ))}
                      {/* Add a larger spacer element to ensure proper scrolling to the end */}
                      <div className="flex-shrink-0 w-16"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-0 sm:mt-4">
            <h2 className="text-lg sm:text-xl font-bold text-secondary mb-3 sm:mb-5">
              {selectedVehicle ? `${selectedVehicle} Assessment Breakdown` : 'Assessment Breakdown '}
            </h2>
            
            {/* Violation Details Table - Keep scrollbars with show-scrollbar class */}
            <div className="bg-slate-50 rounded-xl p-3 sm:p-4 lg:p-5 shadow-[0_10px_15px_-3px_rgba(0,0,0,0.1),0_4px_6px_-2px_rgba(0,0,0,0.05)] mb-6 overflow-hidden">
              <div className="flex justify-between items-center mb-4">
                {/* <h3 className="text-md sm:text-lg font-semibold text-secondary">
                  {selectedAttempt ? `Attempt ${selectedAttempt.attemptNumber} Detailed Violations` : 'Violation Details'}
                </h3> */}
                {/* {selectedAttempt && (
                  <div className="text-sm text-gray-600">
                    Assessment: {selectedAttempt.assessmentNumber} | Score: {selectedAttempt.score}
                  </div>
                )} */}
              </div>
              
              {console.log('Rendering violation details section:', { 
                isLoading: violationLoading, 
                hasSelectedAttempt: !!selectedAttempt, 
                violationDetailsLength: violationDetails.length,
                violationDetails
              })}
              
              {violationLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-gray-600">Loading violation details...</span>
                </div>
              ) : selectedAttempt && violationDetails.length > 0 ? (
                <div className="bg-white rounded-xl p-3 sm:p-4 lg:p-5 shadow-[0_10px_15px_-3px_rgba(0,0,0,0.1),0_4px_6px_-2px_rgba(0,0,0,0.05)]">
                  {/* Test details section */}
                  {selectedAttempt && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-medium">Score: </span>
                          {selectedAttempt.score !== undefined ? selectedAttempt.score : 'N/A'}
                        </div>
                        {selectedAttempt.testTakenDate && (
                          <div>
                            <span className="font-medium">Date: </span>
                            {new Date(selectedAttempt.testTakenDate).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            }).replace(/\//g, '-')}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Status summary section */}
                  <div className="mb-4 grid grid-cols-2 gap-4">
                    <div className="bg-green-50 p-3 rounded-lg border border-green-100">
                      <h4 className="text-sm font-bold text-green-800 mb-2 flex items-center">
                        <span className="inline-flex items-center justify-center w-5 h-5 mr-2 rounded-full bg-green-100">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </span>
                        PASSED
                      </h4>
                      <div className="text-xs text-green-700">
                        {violationDetails.filter(v => v.ViolationQuantity <= 0).length} items
                      </div>
                    </div>
                    <div className="bg-red-50 p-3 rounded-lg border border-red-100">
                      <h4 className="text-sm font-bold text-red-800 mb-2 flex items-center">
                        <span className="inline-flex items-center justify-center w-5 h-5 mr-2 rounded-full bg-red-100">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </span>
                        FAILED
                      </h4>
                      <div className="text-xs text-red-700">
                        {violationDetails.filter(v => v.ViolationQuantity > 0).length} items
                      </div>
                    </div>
                  </div>
                  
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100 sticky top-0 z-10">
                      <tr>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider sticky top-0 bg-gray-100 z-20">
                          Violation Name
                        </th>
                        <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider sticky top-0 bg-gray-100 z-20">
                          Mistake done
                        </th>
                        <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider sticky top-0 bg-gray-100 z-20">
                          No. of violation
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {violationDetails.length > 0 ? (
                        violationDetails.map((violation, index) => {
                          console.log('Rendering violation at index', index, ':', violation);
                          const violationQuantity = violation.ViolationQuantity !== undefined ? violation.ViolationQuantity : 0;
                          const violationName = violation.ViolationScreenName || 'Unknown Violation';
                          // Use server-provided description first, then fall back to client-side description
                          const violationDescription = violation.ViolationDescription || violationDescriptions[violationName] || 'Description not available';
                          
                          return (
                            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-4 py-3 whitespace-normal text-sm text-gray-800 group relative">
                                <div className="cursor-help flex items-center">
                                  <span>{violationName}</span>
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  <div className="hidden group-hover:block absolute z-50 left-0 top-full mt-1 px-4 py-2 w-64 text-xs text-white bg-gray-800 rounded-md shadow-lg">
                                    {violationDescription}
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                {violationQuantity <= 0 ? (
                                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-100">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                                {violationQuantity <= 0 ? (
                                  <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                    PASS
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                    {violationQuantity} FAIL{violationQuantity > 1 ? 'S' : ''}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="3" className="px-4 py-3 text-center text-sm text-gray-600">
                            No violations recorded for this attempt.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : selectedAttempt ? (
                <div className="flex items-center justify-center py-4 text-gray-600">
                  <p>No violation details available for this attempt.</p>
                </div>
              ) : (
                <div className="flex items-center justify-center py-4 text-gray-600">
                  <p>Select an attempt to view detailed violation data.</p>
                </div>
              )}
            </div>

            {selectedVehicle === 'Forklift' && (
              <div className="bg-slate-50 rounded-xl p-3 sm:p-4 lg:p-5 shadow-[0_10px_15px_-3px_rgba(0,0,0,0.1),0_4px_6px_-2px_rgba(0,0,0,0.05)]">
                <div className="flex flex-col items-center justify-center py-4 sm:py-6">
                  <h3 className="text-md font-medium text-gray-700 mb-2">Additional data coming soon</h3>
                  <p className="text-xs sm:text-sm text-gray-600 text-center max-w-md">
                    Enhanced analytics for this vehicle type will be available in future updates.
                  </p>
                </div>
              </div>
            )}
            
            {selectedVehicle && selectedVehicle !== 'Forklift' && (
              <div className="bg-slate-50 rounded-xl p-3 sm:p-4 lg:p-5 shadow-[0_10px_15px_-3px_rgba(0,0,0,0.1),0_4px_6px_-2px_rgba(0,0,0,0.05)]">
                <div className="flex flex-col items-center justify-center py-4 sm:py-6">
                  <h3 className="text-md font-medium text-gray-700 mb-2">Assessment data is being prepared</h3>
                  <p className="text-xs sm:text-sm text-gray-600 text-center max-w-md">
                    Detailed breakdown for this vehicle type will be available soon. Check back later for the complete assessment.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Add scrollbar styles if they don't exist */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.05);
          border-radius: 10px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.15);
          border-radius: 10px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.25);
        }
        
        .custom-scrollbar-horizontal::-webkit-scrollbar {
          height: 6px;
        }
        
        .custom-scrollbar-horizontal::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.05);
          border-radius: 10px;
        }
        
        .custom-scrollbar-horizontal::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.15);
          border-radius: 10px;
        }
        
        .custom-scrollbar-horizontal::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.25);
        }
      `}</style>
      
      {/* Assessment Warning Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Assessment Not Completed"
        message={modalMessage}
      />
    </div>
  );
}

export default MainContent;